import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from './Header';
import Button from './Button';
import CountryCode from './CountryCode';
import productsPricing from '../data/productsPricing';
import { findCurrencySymbol } from '../utils/localeUtils';
import './CartSummary.css';

import companyLogo from '../images/B_longtermtec_logo.png';

const CartSummary = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const queryParams = new URLSearchParams(location.search);
    const locale = queryParams.get('locale') || 'en_US';
    const currencySymbol = findCurrencySymbol(locale);
    const products = productsPricing[locale] || [];

    const [cart, setCart] = useState([]);
    const [country, setCountry] = useState('');
    const [coupon, setCoupon] = useState('');
    const [showCouponInput, setShowCouponInput] = useState(false);
    const [spi, setSpi] = useState('');
    const [isCheckingOut, setIsCheckingOut] = useState(false);

    useEffect(() => {
        const storedCart = JSON.parse(sessionStorage.getItem('cart')) || [];
        setCart(storedCart);

        const spiParam = sessionStorage.getItem('spi');
        if (spiParam) {
            setSpi(spiParam);
        }
    }, []);

    const getProductDetails = (sku) => {
        return products.find(p => p.sku === sku) || {};
    };

    const formatPrice = (price) => {
        if (!price) return `${currencySymbol} 0.00`;
        return `${currencySymbol} ${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const getTermLabel = (term) => {
        switch (term) {
            case 'Monthly': return '/ month';
            case 'Yearly': return '/ year';
            case '3 Years': return '/ 3 years';
            default: return '';
        }
    };

    const updateQuantity = (index, newQty) => {
        if (newQty < 1) return;
        const updatedCart = [...cart];
        updatedCart[index].qty = newQty;
        setCart(updatedCart);
        sessionStorage.setItem('cart', JSON.stringify(updatedCart));
    };

    const removeItem = (index) => {
        const updatedCart = cart.filter((_, i) => i !== index);
        setCart(updatedCart);
        sessionStorage.setItem('cart', JSON.stringify(updatedCart));
    };

    const clearCart = () => {
        setCart([]);
        sessionStorage.removeItem('cart');
    };

    const calculateSubtotal = () => {
        return cart.reduce((total, item) => {
            return total + (item.price * item.qty);
        }, 0);
    };

    const handleCheckout = () => {
        setIsCheckingOut(true);

        // Extract country code from locale (e.g., 'en_US' -> 'US', 'en_CA' -> 'CA')
        const countryCode = locale.split('_')[1] || 'US';
        let url = `https://www.plm.automation.siemens.com/offer/configurable_cart?country=${countryCode}`;

        cart.forEach((item) => {
            let term = 'yearly';
            let length = 1;

            switch (item.subscriptionTerm) {
                case 'Monthly':
                    term = 'monthly';
                    break;
                case 'Yearly':
                    term = 'yearly';
                    length = 1;
                    break;
                case '3 Years':
                    term = 'yearly';
                    length = 3;
                    break;
                default:
                    term = 'yearly';
                    length = 1;
            }

            url += `&sku=${item.sku}&qty=${item.qty}&term=${term}&length=${length}`;
        });

        if (coupon.trim() !== '') {
            url += `&coupon=${encodeURIComponent(coupon.trim())}`;
        }

        // Set spi based on locale: CA = 4077332, US = 4431814
        const spiValue = countryCode === 'CA' ? '4077332' : '4431814';
        url += `&spi=${spiValue}`;

        url += '&create_cart=Create+Cart';
        window.open(url, '_blank');
        setIsCheckingOut(false);
    };

    const handleContinueShopping = () => {
        // Navigate back based on locale
        if (locale === 'en_CA') {
            navigate('/cad');
        } else {
            navigate('/usd');
        }
    };

    if (cart.length === 0) {
        return (
            <div className="cart-summary-page">
                <Header title="Shopping Cart" logo={companyLogo} />
                <div className="cart-summary-container">
                    <div className="empty-cart">
                        <h2>Your cart is empty</h2>
                        <p>Add some products to get started!</p>
                        <Button label="Continue Shopping" onClick={handleContinueShopping} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="cart-summary-page">
            <Header title="Shopping Cart" logo={companyLogo} />
            <div className="cart-summary-container">
                <div className="cart-header">
                    <h2>Your Cart ({cart.length} item{cart.length !== 1 ? 's' : ''})</h2>
                    <button className="clear-all-btn" onClick={clearCart}>Clear All</button>
                </div>

                <div className="cart-items">
                    {cart.map((item, index) => {
                        const productDetails = getProductDetails(item.sku);
                        return (
                            <div key={index} className="cart-item">
                                <div className="item-image">
                                    <img src={productDetails.image} alt={productDetails.name || item.sku} />
                                </div>
                                <div className="item-details">
                                    <h3>{productDetails.name || item.sku}</h3>
                                    <p className="item-sku">SKU: {item.sku}</p>
                                    <p className="item-term">{item.subscriptionTerm} subscription</p>
                                    {productDetails.prerequisites && productDetails.prerequisites.length > 0 && (
                                        <div className="prerequisite-warning">
                                            <span className="warning-icon">⚠</span>
                                            <span>Prerequisite product required.</span>
                                        </div>
                                    )}
                                </div>
                                <div className="item-quantity">
                                    <button
                                        className="qty-btn"
                                        onClick={() => updateQuantity(index, item.qty - 1)}
                                    >
                                        -
                                    </button>
                                    <span className="qty-value">{item.qty}</span>
                                    <button
                                        className="qty-btn"
                                        onClick={() => updateQuantity(index, item.qty + 1)}
                                    >
                                        +
                                    </button>
                                </div>
                                <div className="item-price">
                                    <p className="price-amount">{formatPrice(item.price * item.qty)}</p>
                                    <p className="price-term">{getTermLabel(item.subscriptionTerm)}</p>
                                </div>
                                <button
                                    className="remove-btn"
                                    onClick={() => removeItem(index)}
                                    aria-label="Remove item"
                                >
                                    &times;
                                </button>
                            </div>
                        );
                    })}
                </div>

                <div className="cart-footer">
                    <div className="cart-options">
                        <CountryCode onCountryChange={setCountry} />

                        <div className="coupon-section">
                            {!showCouponInput ? (
                                <button
                                    className="coupon-toggle"
                                    onClick={() => setShowCouponInput(true)}
                                >
                                    Have a coupon code?
                                </button>
                            ) : (
                                <div className="coupon-input-group">
                                    <input
                                        type="text"
                                        value={coupon}
                                        onChange={(e) => setCoupon(e.target.value)}
                                        placeholder="Enter coupon code"
                                        className="coupon-input"
                                    />
                                    <button
                                        className="coupon-clear"
                                        onClick={() => {
                                            setCoupon('');
                                            setShowCouponInput(false);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="cart-total">
                        <div className="subtotal-row">
                            <span>Subtotal:</span>
                            <span className="subtotal-amount">{formatPrice(calculateSubtotal())}</span>
                        </div>
                        <p className="tax-note">Taxes calculated at checkout</p>
                    </div>

                    <div className="cart-actions">
                        <Button label="Continue Shopping" onClick={handleContinueShopping} />
                        <Button
                            label={isCheckingOut ? "Processing..." : "Proceed to Checkout"}
                            onClick={handleCheckout}
                            disabled={isCheckingOut}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CartSummary;
